import { NP_TEST_PLAN_CONDITIONS } from './npTestPlanConditions'
import { NP_TEST_PLAN_UNBOUNDED_AGE } from './npTestPlanMeasureUtils'
import {
  NpTestPlanGradeLevel,
  NpTestPlanInformant,
  NpTestPlanMeasure,
  NpTestPlanMeasureType,
  NpTestPlanModality,
  NpTestPlanStatus,
  NpTestPlanSummary,
} from './npTestPlanTypes'

export interface ApiTestMeasure {
  id: string
  name: string
  type: NpTestPlanMeasureType
  domain: string | null
  format: string | null
  ci_relationship_types: string[] | null
  age_range_min: number | null
  age_range_max: number | null
  estimated_duration_min_minutes: number | null
  estimated_duration_max_minutes: number | null
}

export interface ApiTestMeasureListResponse {
  test_measures: ApiTestMeasure[]
}

export interface ApiStandardBatteryResponse {
  battery: { id: string; test_measures: ApiTestMeasure[] }
}

export interface ApiTestPlanMeasureInput {
  measure_id: string
  sequence_order: number
}

export interface ApiTestPlanInput {
  modality?: string
  age?: number | null
  condition?: string
  grade_level?: number | null
  battery_id?: string
  uses_standard_battery?: boolean
  measures?: ApiTestPlanMeasureInput[]
}

export interface ApiTestPlan {
  id: string
  episode_id: string
  patient_id: string
  modality: string
  condition: string | null
  status: string
  test_plan_measures: { measure_id: string; sequence_order: number; test_measure: ApiTestMeasure }[]
}

const API_MODALITIES: Record<NpTestPlanModality, string> = {
  [NpTestPlanModality.VIRTUAL]: 'virtual',
  [NpTestPlanModality.IN_PERSON]: 'in_person',
}

const API_INFORMANTS: Record<string, NpTestPlanInformant> = {
  self_report: NpTestPlanInformant.SELF_REPORT,
  caregiver: NpTestPlanInformant.CAREGIVER,
  teacher: NpTestPlanInformant.TEACHER,
}

export const toApiModality = (modality: NpTestPlanModality) => API_MODALITIES[modality]

const fromApiModality = (modality: string) =>
  modality === API_MODALITIES[NpTestPlanModality.IN_PERSON] ? NpTestPlanModality.IN_PERSON : NpTestPlanModality.VIRTUAL

/** `grade1` → `grade_1`, `preK` → `pre_k`. Post-secondary has no LyraTherapy grade, so it is not sent. */
export const toApiGradeLevel = (gradeLevel?: NpTestPlanGradeLevel): string | undefined =>
  gradeLevel && gradeLevel !== NpTestPlanGradeLevel.POST_SECONDARY
    ? gradeLevel.replace(/([A-Z]|\d+)/g, '_$1').toLowerCase()
    : undefined

/** The plan stores LyraTherapy's `GradeLevel` value: pre-K is 1, kindergarten 2, and grade n is n + 2. */
export const toApiGradeLevelValue = (gradeLevel?: NpTestPlanGradeLevel): number | null => {
  switch (gradeLevel) {
    case undefined:
    case NpTestPlanGradeLevel.POST_SECONDARY:
      return null
    case NpTestPlanGradeLevel.PRE_K:
      return 1
    case NpTestPlanGradeLevel.KINDERGARTEN:
      return 2
    default:
      return Number(gradeLevel.replace('grade', '')) + 2
  }
}

export const toApiMeasures = (measureIds: string[]): ApiTestPlanMeasureInput[] =>
  measureIds.map((measure_id, sequence_order) => ({ measure_id, sequence_order }))

// The UI groups each measure under one informant, so a multi-informant measure goes under its first.
const toInformant = (ciRelationshipTypes: string[] | null): NpTestPlanInformant => {
  const [first] = ciRelationshipTypes ?? []
  return first ? (API_INFORMANTS[first] ?? NpTestPlanInformant.OTHER) : NpTestPlanInformant.SELF_REPORT
}

export const toNpTestPlanMeasure = (measure: ApiTestMeasure): NpTestPlanMeasure => ({
  id: measure.id,
  name: measure.name,
  measureType: measure.type,
  estimatedTimeMin: measure.estimated_duration_min_minutes ?? measure.estimated_duration_max_minutes ?? 0,
  estimatedTimeMax: measure.estimated_duration_max_minutes ?? measure.estimated_duration_min_minutes ?? 0,
  informant: toInformant(measure.ci_relationship_types),
  category: measure.domain ?? '',
  sessionFormat: measure.format ?? '',
  minAge: measure.age_range_min ?? 0,
  maxAge: measure.age_range_max ?? NP_TEST_PLAN_UNBOUNDED_AGE,
})

/** Before a plan exists the episode reads as not started, which LyraTherapy answers with a 404. */
export const toNotStartedSummary = ({
  episodeId,
  patientId,
}: {
  episodeId: string
  patientId: string
}): NpTestPlanSummary => ({
  episodeId,
  patientId,
  planId: null,
  status: NpTestPlanStatus.NOT_STARTED,
  dueDate: null,
  modality: null,
  measures: [],
  battery: null,
})

export const toNpTestPlanSummary = (plan: ApiTestPlan): NpTestPlanSummary => {
  const modality = fromApiModality(plan.modality)
  const condition = NP_TEST_PLAN_CONDITIONS.find(({ id }) => id === plan.condition)
  return {
    episodeId: plan.episode_id,
    patientId: plan.patient_id,
    planId: plan.id,
    // This client has no signed status yet, so every saved plan reads as a draft.
    status: NpTestPlanStatus.DRAFT,
    dueDate: null,
    modality,
    measures: [
      ...new Map(
        [...plan.test_plan_measures]
          .sort((a, b) => a.sequence_order - b.sequence_order)
          .map(({ measure_id, test_measure }) => [measure_id, toNpTestPlanMeasure(test_measure)]),
      ).values(),
    ],
    battery: { modality, conditionName: condition?.name ?? plan.condition ?? '' },
  }
}
